import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/cn.js';

export type PillVariant =
  | 'service'
  | 'install'
  | 'pm'
  | 'emergency'
  | 'sales'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger'
  | 'matrix'
  | 'neutral';

interface PillProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: PillVariant;
  size?: 'sm' | 'md';
  dot?: boolean;
  children?: ReactNode;
}

// Pill styling driven by CSS vars from index.css so each variant matches the
// exact bg/fg pair used in the /Design/ mockups.
const VARIANT_STYLE: Record<PillVariant, { bg: string; fg: string; dot?: string }> = {
  service: { bg: 'var(--pill-service-bg)', fg: 'var(--pill-service-fg)', dot: 'var(--trade-service)' },
  install: { bg: 'var(--pill-install-bg)', fg: 'var(--pill-install-fg)', dot: 'var(--trade-install)' },
  pm: { bg: 'var(--pill-pm-bg)', fg: 'var(--pill-pm-fg)', dot: 'var(--trade-pm)' },
  emergency: { bg: 'var(--pill-emergency-bg)', fg: 'var(--pill-emergency-fg)', dot: 'var(--trade-emergency)' },
  sales: { bg: 'var(--pill-sales-bg)', fg: 'var(--pill-sales-fg)', dot: 'var(--trade-sales)' },
  // Status / semantic variants reuse the trade palette so the language is
  // consistent: green = good, blue = info, amber = warning, red = danger.
  success: { bg: 'var(--pill-pm-bg)', fg: 'var(--pill-pm-fg)', dot: 'var(--trade-pm)' },
  info: { bg: 'var(--pill-service-bg)', fg: 'var(--pill-service-fg)', dot: 'var(--trade-service)' },
  warning: { bg: 'var(--pill-sales-bg)', fg: 'var(--pill-sales-fg)', dot: 'var(--trade-sales)' },
  danger: { bg: 'var(--pill-emergency-bg)', fg: 'var(--pill-emergency-fg)', dot: 'var(--trade-emergency)' },
  matrix: { bg: 'var(--pill-install-bg)', fg: 'var(--pill-install-fg)' },
  neutral: { bg: 'var(--pill-neutral-bg)', fg: 'var(--pill-neutral-fg)' },
};

export function Pill({
  variant = 'neutral',
  size = 'sm',
  dot = false,
  className,
  children,
  style,
  ...rest
}: PillProps) {
  const tone = VARIANT_STYLE[variant];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full font-medium whitespace-nowrap',
        size === 'sm' ? 'text-[10px] px-2 py-0.5 leading-4' : 'text-xs px-2.5 py-1',
        className,
      )}
      style={{ backgroundColor: tone.bg, color: tone.fg, ...style }}
      {...rest}
    >
      {dot && tone.dot && (
        <span
          aria-hidden
          className="inline-block h-1.5 w-1.5 rounded-full"
          style={{ backgroundColor: tone.dot }}
        />
      )}
      {children}
    </span>
  );
}
